/**
 * CookieConsent.tsx
 * Thread: T-051 — Cookie & LocalStorage Consent Banner
 *
 * Premium glassmorphism consent banner — 3-button compact layout with expandable settings.
 * Buttons: קבל הכל / התאמה אישית ▼ / דחייה
 * 24-hour re-prompt for partial/decline consent.
 * RTL-first Hebrew UI. Uses framer-motion for smooth height and slide-in animations.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, User, BarChart2, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getPreferences,
  shouldShowBanner,
  setPreferences,
} from '@/services/ConsentService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CategoryKey = 'functional' | 'analytics';

// ---------------------------------------------------------------------------
// Toggle Switch Component
// ---------------------------------------------------------------------------

interface ToggleProps {
  id: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const Toggle: React.FC<ToggleProps> = ({ id, checked, onChange, disabled = false }) => (
  <button
    id={id}
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative w-10 h-6 rounded-full transition-all duration-300 shrink-0 focus:outline-none focus:ring-2 focus:ring-gold-500/40 ${
      disabled
        ? 'bg-mystic-800/40 cursor-not-allowed opacity-50'
        : checked
        ? 'bg-gold-500 shadow-[0_0_12px_rgba(234,179,8,0.35)]'
        : 'bg-mystic-700/60 border border-white/10 cursor-pointer hover:bg-mystic-600/60'
    }`}
  >
    <span
      className={`absolute top-0.5 w-5 h-5 rounded-full shadow-md transition-all duration-300 ${
        checked && !disabled ? 'left-[18px] bg-mystic-900' : 'left-0.5 bg-mystic-400'
      }`}
    />
  </button>
);

// ---------------------------------------------------------------------------
// Category Row Component
// ---------------------------------------------------------------------------

interface CategoryRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  toggleId: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  locked?: boolean;
  lockedLabel?: string;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  icon,
  title,
  description,
  toggleId,
  checked,
  onChange,
  locked = false,
  lockedLabel,
}) => (
  <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0" dir="rtl">
    <div className="mt-0.5 shrink-0 text-gold-400/70">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-white leading-tight">{title}</p>
      <p className="text-xs text-mystic-400 mt-0.5 leading-relaxed">{description}</p>
    </div>
    <div className="shrink-0 mt-0.5">
      {locked ? (
        <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-mystic-700/40 border border-white/5 text-mystic-500 select-none whitespace-nowrap">
          {lockedLabel}
        </span>
      ) : (
        <Toggle id={toggleId} checked={checked} onChange={onChange} />
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main CookieConsent Component
// ---------------------------------------------------------------------------

export const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [toggles, setToggles] = useState<Record<CategoryKey, boolean>>({
    functional: false,
    analytics: false,
  });

  /** Load current stored preferences into state */
  const syncTogglesToStorage = () => {
    const current = getPreferences();
    setToggles({ functional: current.functional, analytics: current.analytics });
  };

  // Auto-show respects 24-hour logic
  useEffect(() => {
    if (shouldShowBanner()) {
      const timer = setTimeout(() => {
        syncTogglesToStorage();
        setVisible(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Admin debug listener
  useEffect(() => {
    const handleForceOpen = () => {
      syncTogglesToStorage();
      setVisible(true);
    };
    window.addEventListener('open-cookie-consent', handleForceOpen);
    return () => window.removeEventListener('open-cookie-consent', handleForceOpen);
  }, []);

  const handleSave = (overrides?: Partial<Record<CategoryKey, boolean>>) => {
    const toSave = overrides ?? toggles;
    setPreferences({ functional: toSave.functional ?? false, analytics: toSave.analytics ?? false });
    setVisible(false);
    setIsExpanded(false);
  };

  const handleAcceptAll = () => handleSave({ functional: true, analytics: true });
  const handleDeclineAll = () => handleSave({ functional: false, analytics: false });

  const toggleCategory = (key: CategoryKey) => (value: boolean) =>
    setToggles((prev) => ({ ...prev, [key]: value }));

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Desktop Banner: Floating at bottom-left */}
          <motion.div
            key="consent-desktop"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            aria-label="בחירת העדפות פרטיות"
            role="dialog"
            aria-modal="true"
            className="
              hidden sm:block
              fixed bottom-5 left-5 z-[9000]
              w-full max-w-md
              glass-card
              bg-[#110822]/90
              backdrop-blur-xl
              border border-white/10
              shadow-[0_8px_40px_0_rgba(0,0,0,0.6)]
              p-5
            "
          >
            <ConsentContent
              toggles={toggles}
              onToggle={toggleCategory}
              onSave={() => handleSave()}
              onAcceptAll={handleAcceptAll}
              onDeclineAll={handleDeclineAll}
              isExpanded={isExpanded}
              onToggleExpand={() => setIsExpanded(!isExpanded)}
            />
          </motion.div>

          {/* Mobile Banner: Responsive bottom sheet */}
          <motion.div
            key="consent-mobile"
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 280 }}
            aria-label="בחירת העדפות פרטיות"
            role="dialog"
            aria-modal="true"
            className="
              block sm:hidden
              fixed bottom-0 left-0 right-0 z-[9000]
              bg-[#110822]/95
              backdrop-blur-xl
              border-t border-white/10
              shadow-[0_-8px_40px_0_rgba(0,0,0,0.6)]
              rounded-t-2xl
              p-5 pb-8
            "
          >
            <ConsentContent
              toggles={toggles}
              onToggle={toggleCategory}
              onSave={() => handleSave()}
              onAcceptAll={handleAcceptAll}
              onDeclineAll={handleDeclineAll}
              isExpanded={isExpanded}
              onToggleExpand={() => setIsExpanded(!isExpanded)}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// Inner Content Component (Shared Layout)
// ---------------------------------------------------------------------------

interface ConsentContentProps {
  toggles: Record<CategoryKey, boolean>;
  onToggle: (key: CategoryKey) => (value: boolean) => void;
  onSave: () => void;
  onAcceptAll: () => void;
  onDeclineAll: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const ConsentContent: React.FC<ConsentContentProps> = ({
  toggles,
  onToggle,
  onSave,
  onAcceptAll,
  onDeclineAll,
  isExpanded,
  onToggleExpand,
}) => (
  <div dir="rtl">
    {/* Header */}
    <div className="flex items-center gap-2 mb-3">
      <ShieldCheck className="w-5 h-5 text-gold-400 shrink-0" />
      <h2 className="text-sm font-bold text-white leading-tight">
        העדפות פרטיות ואחסון מקומי
      </h2>
    </div>

    {/* Intro Summary */}
    <p className="text-xs text-mystic-400 leading-relaxed mb-4">
      אנו משתמשים באחסון מקומי לשיפור חווית הגלישה והתאמה אישית של תכני האתר. באפשרותך לאשר את כלל השימושים או להתאים את העדפותיך באופן מפורט.
    </p>

    {/* ─── Compact 3-Button Layout ─── */}
    <div className="flex flex-row items-center gap-2">
      {/* Accept All (Gold, primary) */}
      <button
        id="consent-accept-all"
        onClick={onAcceptAll}
        className="
          flex-1 text-xs font-bold py-2 px-3 rounded-xl
          bg-gradient-to-r from-gold-600 to-amber-500
          text-mystic-900
          hover:from-gold-500 hover:to-amber-400
          active:scale-95
          transition-all duration-150 shadow-md
          whitespace-nowrap
        "
      >
        קבל הכל
      </button>

      {/* Customize Toggle Button (Middle) */}
      <button
        id="consent-toggle-settings"
        onClick={onToggleExpand}
        className="
          flex-1 flex items-center justify-center gap-1.5
          text-xs font-medium py-2 px-3 rounded-xl
          bg-mystic-800/60 border border-white/10
          hover:bg-mystic-700/80 text-white
          transition-all duration-150
          whitespace-nowrap
        "
      >
        התאמה אישית
        {isExpanded ? (
          <ChevronUp className="w-3.5 h-3.5 text-gold-400" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-mystic-400" />
        )}
      </button>

      {/* Decline All (Subdued text / border) */}
      <button
        id="consent-decline-all"
        onClick={onDeclineAll}
        className="
          flex-1 text-xs font-medium py-2 px-3 rounded-xl
          bg-transparent hover:bg-white/5
          border border-white/5 hover:border-white/15
          text-mystic-400 hover:text-mystic-200
          transition-all duration-150
          whitespace-nowrap
        "
      >
        דחייה
      </button>
    </div>

    {/* ─── Settings Accordion Panel ─── */}
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          key="settings-accordion"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden mt-4 bg-mystic-950/45 border border-white/5 rounded-xl p-3"
        >
          <div className="space-y-1">
            {/* Essential — locked with "אין ברירה" label */}
            <CategoryRow
              icon={<Lock className="w-4 h-4" />}
              title="הכרחי"
              description="מפתחות אימות Supabase ואחסון בחירת ההעדפות עצמן. לא ניתן לביטול."
              toggleId="consent-toggle-essential"
              checked={true}
              onChange={() => {}}
              locked
              lockedLabel="חובה"
            />

            {/* Functional */}
            <CategoryRow
              icon={<User className="w-4 h-4" />}
              title="חווית משתמש והעדפות"
              description="שמירת ערים אחרונות, הגדרות מחשבון, בחירות שמורות ותצוגה מותאמת אישית."
              toggleId="consent-toggle-functional"
              checked={toggles.functional}
              onChange={onToggle('functional')}
            />

            {/* Analytics */}
            <CategoryRow
              icon={<BarChart2 className="w-4 h-4" />}
              title="מדדי ביצועים ואנליטיקה"
              description="מעקב פעילות אחרונה, ניתוח דפי ניווט ורישום שגיאות לשיפור השירות."
              toggleId="consent-toggle-analytics"
              checked={toggles.analytics}
              onChange={onToggle('analytics')}
            />
          </div>

          {/* Save Selection Button */}
          <button
            id="consent-save-selection"
            onClick={onSave}
            className="
              w-full mt-3 text-xs font-semibold py-2 px-4 rounded-xl
              border border-gold-500/40 text-gold-300
              hover:bg-gold-500/10 transition-all duration-150
            "
          >
            שמור בחירה
          </button>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Privacy Link */}
    <div className="mt-4 text-center">
      <a
        href="/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[11px] text-mystic-600 hover:text-gold-400 transition-colors underline underline-offset-2"
        id="consent-privacy-link"
      >
        מדיניות פרטיות
      </a>
    </div>
  </div>
);
